import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../services/store';

import { PURGE } from 'redux-persist';

import { LibraryState } from '../types/library';
import { selectResourceById } from '../services/drive';
import { SortDirection, SortType } from '../types/common';

const initialState: LibraryState = {
  page: 0,
  options: {
    sortType: SortType.REG_DATE,
    sortOrder: SortDirection.DESC,
    layoutType: 'grid',
    isEditMode: false,
    isDetailsOpened: false,
    isShowShared: true,
  },
  selectedIds: [],
  selectedItem: null,
};

export const driveSlice = createSlice({
  name: 'drive',
  initialState,
  reducers: {
    drivePageChanged: (state, action) => {
      state.page = action.payload;
    },
    driveSortTypeChanged: (state, action) => {
      state.options.sortType = action.payload;
    },
    driveOrderChanged: (
      state,
      action: PayloadAction<SortDirection, string>
    ) => {
      state.options.sortOrder = action.payload;
    },
    driveLayoutTypeChanged: (state, action) => {
      state.options.layoutType = action.payload;
    },
    driveEditModeToggled: (state, action) => {
      state.options.isEditMode = action.payload;
    },
    driveDetailsToggled: (
      state,
      action: PayloadAction<boolean | undefined, string>
    ) => {
      state.options.isDetailsOpened =
        action.payload ?? !state.options.isDetailsOpened;
    },
    driveSharedToggled: (state, action) => {
      state.options.isShowShared = action.payload;
    },
    driveItemSelected: (state, action) => {
      state.selectedIds = action.payload.ids;
      if (action.payload.item !== undefined) {
        state.selectedItem = action.payload.item;
      }
    },
    driveIndexListChanged: (state, action) => {
      if (state.selectedItem) {
        state.selectedItem.projectIndex.indexList = action.payload;
      }
    },
    driveIndexIndentChanged: (state, action) => {
      const item = state.selectedItem?.projectIndex.indexList[action.payload];
      if (item) {
        item.indentYn = item.indentYn ? 0 : 1;
      }
    },
    driveShareLinkChanged: (state, action) => {
      if (state.selectedItem) {
        state.selectedItem.share = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  },
});

export const {
  drivePageChanged,
  driveSortTypeChanged,
  driveOrderChanged,
  driveLayoutTypeChanged,
  driveEditModeToggled,
  driveDetailsToggled,
  driveSharedToggled,
  driveItemSelected,
  driveIndexListChanged,
  driveIndexIndentChanged,
  driveShareLinkChanged,
} = driveSlice.actions;

export const selectDrivePage = (state: RootState) => state.drive.page;

export const selectDriveOptions = (state: RootState) => state.drive.options;

export const selectDriveSelectedIds = (state: RootState) =>
  state.drive.selectedIds;

export const selectDriveSelectedId = createSelector(
  selectDriveSelectedIds,
  (selectedIds) => selectedIds[0]
);

export const selectDriveSelectedItems = (state: RootState) =>
  state.drive.selectedIds.map((id) => selectResourceById(state, id));

export const selectDriveSingleItem = createSelector(
  selectDriveSelectedItems,
  (selectedItems) => selectedItems[0]
);

export const selectDriveSelectedItem = (state: RootState) =>
  state.drive.selectedItem;

export const selectDriveResource = createSelector(
  selectDriveSelectedItem,
  (selectedItem) => selectedItem?.resource
);

export default driveSlice.reducer;
